import { useUser } from "@clerk/clerk-expo";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ScrollView, View } from "tamagui";

import { getGetVisitOptionsForAFlowQueryKey } from "@/api/generated";
import {
  AffiliateOutcome,
  type PartnerIntentRequest,
} from "@/api/generated/models";
import { useAttemptAffiliation } from "@/api/mutations/affiliate";
import { useGetUserProfile } from "@/api/queries/";
import {
  Button,
  Footer,
  Header,
  Heading,
  Li,
  Link,
  LoadingSpinner,
  Ol,
  Paragraph,
  Text,
} from "@/components";
import { brandName } from "@/config/brand";
import { captureException } from "@/sentry";
import AffiliateWelcomeScreen from "./AffiliateWelcomeScreen";
import { useUserProgression } from "./UserProgression/UserProgressionContext";
import UserProgressionScreenWrapper, {
  type UserProgressionScreenWrapperProps,
} from "./UserProgression/UserProgressionScreenWrapper";

type AffiliationDecision = "checking" | "welcome" | "remediation" | "no-action";

interface Props {
  onContinue: () => void;
  onReturnToPaymentSelection?: () => void;
}

type RemediationScreenContentProps = {
  onResolved: () => void;
  hasSelectionStep: boolean;
  affiliationDecision: AffiliationDecision;
  newlyAffiliatedPartnerUid: string | undefined;
};

type RemediationScreenWithDialogProps = Props &
  Omit<UserProgressionScreenWrapperProps, "children" | "dialogVariant">;

function formatDateOfBirth(dateOfBirth: string | undefined): string {
  if (!dateOfBirth) return "";
  const [month, day, year] = dateOfBirth.split("/");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const useAffiliationDecision = ({
  remediateNothingFound = false,
}: {
  remediateNothingFound?: boolean;
} = {}): {
  affiliationDecision: AffiliationDecision;
  newlyAffiliatedPartnerUid: string | undefined;
} => {
  const attemptAffiliation = useAttemptAffiliation();
  const partnerIntent = useUserProgression()?.progression.metadata
    ?.partner_intent as PartnerIntentRequest | null | undefined;

  useEffect(() => {
    if (attemptAffiliation.isIdle) {
      attemptAffiliation.mutate(partnerIntent ?? null);
    }
  }, [partnerIntent, attemptAffiliation]);

  useEffect(() => {
    if (attemptAffiliation.isError) {
      captureException(attemptAffiliation.error, {
        extra: {
          context: "RemediationScreen.useAffiliationDecision",
          partner: partnerIntent?.partner ?? null,
        },
      });
    }
  }, [
    attemptAffiliation.isError,
    attemptAffiliation.error,
    partnerIntent?.partner,
  ]);

  // The backend decides whether this progression step applies. Once rendered, map the
  // affiliation result to the UI or advance when no further action is needed.
  if (attemptAffiliation.isSuccess) {
    const newlyAffiliatedPartnerUid =
      attemptAffiliation.data.newly_affiliated_partner_uids?.[0];
    switch (attemptAffiliation.data.outcome) {
      case AffiliateOutcome.newly_created:
        return {
          affiliationDecision: "welcome",
          newlyAffiliatedPartnerUid,
        };
      case AffiliateOutcome.remediation_needed:
        return {
          affiliationDecision: "remediation",
          newlyAffiliatedPartnerUid: undefined,
        };
      case AffiliateOutcome.already_affiliated:
        return {
          affiliationDecision: "no-action",
          newlyAffiliatedPartnerUid: undefined,
        };
      case AffiliateOutcome.nothing_found:
        return {
          affiliationDecision: remediateNothingFound
            ? "remediation"
            : "no-action",
          newlyAffiliatedPartnerUid: undefined,
        };
    }
  }

  if (attemptAffiliation.isError) {
    return {
      affiliationDecision: "no-action",
      newlyAffiliatedPartnerUid: undefined,
    };
  }

  return {
    affiliationDecision: "checking",
    newlyAffiliatedPartnerUid: undefined,
  };
};

const useRemediationResolution = ({
  onContinue,
  onReturnToPaymentSelection,
}: Props) => {
  const queryClient = useQueryClient();
  const progression = useUserProgression()?.progression;
  const flowName = progression?.flow_name;
  const hasSelectionStep = Boolean(onReturnToPaymentSelection);
  const { affiliationDecision, newlyAffiliatedPartnerUid } =
    useAffiliationDecision({ remediateNothingFound: hasSelectionStep });

  useEffect(() => {
    if (affiliationDecision !== "welcome" || !flowName) return;
    // Cached options are priced pre-sponsorship, so they can't paint the reopened screen.
    queryClient.removeQueries({
      queryKey: getGetVisitOptionsForAFlowQueryKey(flowName),
    });
  }, [affiliationDecision, flowName, queryClient]);

  // Activating a membership advances past selection without a pick, so one is still owed.
  const selectionStillOwed =
    progression?.metadata?.partner_matching_requested === true;

  return {
    affiliationDecision,
    newlyAffiliatedPartnerUid,
    hasSelectionStep,
    onResolved: selectionStillOwed
      ? (onReturnToPaymentSelection ?? onContinue)
      : onContinue,
  };
};

function RemediationScreenContent({
  onResolved,
  hasSelectionStep,
  affiliationDecision,
  newlyAffiliatedPartnerUid,
}: RemediationScreenContentProps) {
  const { data: userProfile } = useGetUserProfile();
  const { user } = useUser();

  useEffect(() => {
    if (
      affiliationDecision === "no-action" ||
      (hasSelectionStep && affiliationDecision === "welcome")
    ) {
      onResolved();
    }
  }, [affiliationDecision, onResolved, hasSelectionStep]);

  // also handling "continue" here because we want to show the loading spinner
  // for the moment we're waiting for the useEffect to fire
  if (
    affiliationDecision === "checking" ||
    affiliationDecision === "no-action" ||
    (hasSelectionStep && affiliationDecision === "welcome")
  ) {
    return (
      <LoadingSpinner
        size="$900"
        flex={1}
        alignItems="center"
        justifyContent="center"
      />
    );
  }

  if (affiliationDecision === "welcome") {
    return (
      <AffiliateWelcomeScreen
        onContinue={onResolved}
        partnerUid={newlyAffiliatedPartnerUid}
      />
    );
  }

  const name = [userProfile?.firstName, userProfile?.lastName]
    .filter(Boolean)
    .join(" ");
  const birthday = formatDateOfBirth(userProfile?.dateOfBirth);
  const phone = user?.primaryPhoneNumber?.phoneNumber;

  return (
    <ScrollView
      flex={1}
      contentContainerStyle={{
        flexGrow: 1,
        gap: "$300",
        padding: "$200",
      }}
    >
      <Header gap="$100">
        <Heading size="$xl">
          We couldn't verify your access to {brandName}
        </Heading>
        <Paragraph color="$textSecondary">
          The details you entered don't match what we have on file, but there
          are a few quick ways to fix this.
        </Paragraph>
      </Header>

      <Ol gap="$300">
        <Li>
          <Text bold>Confirm your details with your sponsor</Text>
          <Paragraph color="$textSecondary">
            Please check that your personal details are up to date on your
            sponsor's app or portal.
          </Paragraph>
        </Li>

        <Li>
          <Text bold>Contact {brandName} support</Text>
          <Paragraph color="$textSecondary">
            If the information in your sponsor's app or portal is up to date and
            match what you entered below, please{" "}
            <Link bold href="https://www.curaihealth.com/support" external>
              contact {brandName} support
            </Link>
            .
          </Paragraph>
        </Li>
      </Ol>

      {(name || birthday || phone) && (
        <View
          backgroundColor="$fillClinician"
          borderRadius="$150"
          padding="$200"
        >
          {name && <Text size="$xs">Name: {name}</Text>}
          {birthday && <Text size="$xs">Birthday: {birthday}</Text>}
          {phone && <Text size="$xs">Phone: {phone}</Text>}
        </View>
      )}

      <Footer mode="dialog" marginTop="auto">
        <Button
          constrained
          mode="primary"
          onPress={onResolved}
          dataTrackingId="partner-remediation-continue-without-sponsorship"
        >
          Continue without sponsorship
        </Button>
      </Footer>
    </ScrollView>
  );
}

export function RemediationScreenWithDialog({
  onContinue,
  onReturnToPaymentSelection,
  ...dialogProps
}: RemediationScreenWithDialogProps) {
  const {
    affiliationDecision,
    newlyAffiliatedPartnerUid,
    hasSelectionStep,
    onResolved,
  } = useRemediationResolution({ onContinue, onReturnToPaymentSelection });
  const dialogVariant =
    affiliationDecision === "remediation" ? "default" : "minimal";

  return (
    <UserProgressionScreenWrapper
      {...dialogProps}
      dialogVariant={dialogVariant}
    >
      <RemediationScreenContent
        onResolved={onResolved}
        hasSelectionStep={hasSelectionStep}
        affiliationDecision={affiliationDecision}
        newlyAffiliatedPartnerUid={newlyAffiliatedPartnerUid}
      />
    </UserProgressionScreenWrapper>
  );
}

export default function RemediationScreen({
  onContinue,
  onReturnToPaymentSelection,
}: Props) {
  const {
    affiliationDecision,
    newlyAffiliatedPartnerUid,
    hasSelectionStep,
    onResolved,
  } = useRemediationResolution({ onContinue, onReturnToPaymentSelection });

  return (
    <RemediationScreenContent
      onResolved={onResolved}
      hasSelectionStep={hasSelectionStep}
      affiliationDecision={affiliationDecision}
      newlyAffiliatedPartnerUid={newlyAffiliatedPartnerUid}
    />
  );
}
